import { type StoryFeed, StoryFeedType, StoryStatus } from '@/@types/story'
import type { User } from '@/@types/user'
import clsx from 'clsx'
import { motion, useReducedMotion, type Variants } from 'motion/react'
import type { ComponentType, SVGProps } from 'react'
import StoryPhotoRing from '@/assets/story-ring-photo.svg?react'
import StoryReadRing from '@/assets/story-ring-read.svg?react'
import StoryVideoRing from '@/assets/story-ring-video.svg?react'
import styles from './StoryFeedItem.module.css'

export interface StoryFeedItemProps {
	feed: StoryFeed
	currentUsername: User['username']
	onClick: (feed: StoryFeed) => void
}

export const getStoryFeedItemKey = (feed: StoryFeed) =>
	`${feed.type}:${feed.type === StoryFeedType.USER_STORY ? feed.user.id : feed.data.id}`

const RING_COMPONENT: Partial<Record<StoryStatus, ComponentType<SVGProps<SVGSVGElement>>>> = {
	PICTURE: StoryPhotoRing,
	VIDEO: StoryVideoRing,
	READ: StoryReadRing,
}
const STORY_FEED_ITEM_VARIANTS: Variants = {
	hidden: {
		x: 10,
		opacity: 0,
	},
	visible: {
		x: 0,
		opacity: 1,
		transition: {
			type: 'spring',
			stiffness: 420,
			damping: 32,
			mass: 0.8,
		},
	},
}

export default function StoryFeedItem({ feed, currentUsername, onClick }: StoryFeedItemProps) {
	const prefersReducedMotion = useReducedMotion()
	const isUserStory = feed.type === StoryFeedType.USER_STORY
	const isCurrentUser = isUserStory && feed.user.username === currentUsername
	const label = isCurrentUser ? '我的日记' : isUserStory ? feed.user.screenName : feed.data.title
	const avatarUrl = isUserStory
		? feed.user.avatarImage.picUrl || feed.user.profileImageUrl
		: feed.data.image
	const ringStatus = isUserStory ? (feed.user.storyStatus ?? StoryStatus.NONE) : StoryStatus.PICTURE
	const Ring = RING_COMPONENT[ringStatus]
	const fallbackLabel = Array.from(label.trim())[0] ?? ''
	const accessibleLabel = isCurrentUser
		? '查看我的日记'
		: isUserStory
			? `查看${label}的日记`
			: `打开${label}`
	const hasUnreadStory = ringStatus === 'PICTURE' || ringStatus === 'VIDEO'

	return (
		<motion.li className={styles.item} variants={STORY_FEED_ITEM_VARIANTS}>
			<motion.button
				type="button"
				className={styles.button}
				whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
				onClick={() => onClick(feed)}
				aria-label={accessibleLabel}
				data-clickable-feedback
			>
				<span className={styles.avatarFrame}>
					{avatarUrl ? (
						<img
							className={styles.avatar}
							src={avatarUrl}
							alt=""
							loading="lazy"
							referrerPolicy="no-referrer"
						/>
					) : (
						<span className={styles.avatarFallback} aria-hidden="true">
							{fallbackLabel}
						</span>
					)}
					{Ring && <Ring className={styles.ring} aria-hidden="true" />}
				</span>
				<span
					className={clsx(styles.label, (isCurrentUser || hasUnreadStory) && styles.unreadLabel)}
					title={label}
				>
					{label}
				</span>
			</motion.button>
		</motion.li>
	)
}
